import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import SMCTextInput from '../../../../components/Simple/SMCTextInput';
import {RoleGetSchema} from '../../../../api/DBSchema/Role';
import SMCTable from '../../../../components/core/SMCTable/SMCTable';
import Sepperator from '../../../../components/Simple/Sepperator';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import EmptyRow from '../../../../components/Simple/EmptyRow';
import ButtonContainer from '../../../../components/Simple/ButtonContainer';
import SMCButton from '../../../../components/Simple/SMCButton';
import RoleRuleEditor from '../../../PopupPages/RoleRuleEditor';
import {useBoolean} from '../../../../utils/hooks';
import {replaceItemInIndex, addItemToArray, hasValue} from '../../../../utils/utils';


const CountCell = React.memo(({index}) => {
    return index + 1;
});

const OperationsCell = React.memo(({value}) => {
    const { t } = useTranslation();
    if (Array.isArray(value)) {
        return value.map(operation => {
            switch (operation) {
                case 'READ':
                    return t('SMP_ROLES_ROLE_RULE_WIZ_RULE_TYPE_READ')
                case 'MODIFY':
                    return t('SMP_ROLES_ROLE_RULE_WIZ_RULE_TYPE_MODIFY')
                case 'CREATE':
                    return t('SMP_ROLES_ROLE_RULE_WIZ_RULE_TYPE_CREATE')
                case 'DELETE':
                    return t('SMP_ROLES_ROLE_RULE_WIZ_RULE_TYPE_DELETE')
                default:
                    return operation
            }
        }).join(', ');
    }
    return null;
});

const ObjectTypeCell = React.memo(({value}) => {
    const { t } = useTranslation();
    switch (value) {
        case 'ANY':
            return '*'
        case 'USER':
            return t('com.sofaware.util.TYPE_USER')
        case 'CPSMPPLAN':
            return t('com.sofaware.util.TYPE_TEMPLATE')
        case 'COMMUNITY':
            return t('com.sofaware.util.TYPE_COMMUNITY')
        case 'CPSMPGATEWAY':
            return t('com.sofaware.util.TYPE_GATEWAY')
        case 'ROLE':
            return t('com.sofaware.util.TYPE_ROLE')
        case 'POLICY':
            return t('com.sofaware.util.TYPE_POLICY')
        case 'CUSTOM_IMAGE':
            return t('com.sofaware.util.TYPE_CUSTOM_IMAGE')
        case 'FIRMWARE':
            return t('com.sofaware.util.TYPE_FIRM')
        case 'PORTAL_SETTINGS':
            return t('com.sofaware.util.TYPE_SETTINGS')
        default:
            return value;
    }
});


const pageSchema = RoleGetSchema.pages.general;
const pageId = pageSchema.pageId;

function RoleGeneral({
    roleObject,
    setRoleData
}) {
    const { t } = useTranslation();

    // Page vars
    const pageData = roleObject[pageId];
    const setPageValue = useCallback((paramName, value) => {
        setRoleData([pageId, paramName], value);
    }, [setRoleData]);

    const description  = pageData[pageSchema.description];
    const rules        = pageData[pageSchema.rules];
    const rulesSchema  = pageSchema.rulesSchema;

    // Table vars
    const [selectedObjects, setSelectedObjects] = useState([]);

    const removeSelectedObjects = useCallback(() => {
        if (selectedObjects && selectedObjects.length) {
            setPageValue(pageSchema.rules, rules.filter((elem, i) => {
                const normalizedIndex = i + 1;
                return selectedObjects.indexOf(normalizedIndex) === -1;
            }));
        }
        setSelectedObjects([]);
    }, [selectedObjects, rules, setPageValue]);

    // Editor vars
    const [isRoleRuleEditorOpen, openRoleRuleEditor, closeRoleRuleEditor] = useBoolean(false);
    const [currentlyEditedRole, setCurrentlyEditedRole] = useState(null);
    const [currentlyEditedRoleIndex, setCurrentlyEditedRoleIndex] = useState(null);

    const roleEditorOnSuccess = useCallback(val => {
        if (hasValue(currentlyEditedRoleIndex)) {
            setPageValue(pageSchema.rules, replaceItemInIndex(rules, val, currentlyEditedRoleIndex));
        }
        else {
            setPageValue(pageSchema.rules, addItemToArray(rules, val));
        }
        setCurrentlyEditedRole(null);
        setCurrentlyEditedRoleIndex(null);
        closeRoleRuleEditor();
    }, [rules, setPageValue, currentlyEditedRoleIndex, closeRoleRuleEditor]);

    const roleEditorOnCancel = useCallback(() => {
        setCurrentlyEditedRole(null);
        setCurrentlyEditedRoleIndex(null);
        closeRoleRuleEditor();
    }, [closeRoleRuleEditor]);

    const PermissionCell = React.memo(({value, object, index}) => {
        switch (value) {
            case 'ALLOW':
                return (
                    <span
                        style={{ color: 'green', cursor: 'pointer' }}
                        onClick={() => {
                            setCurrentlyEditedRole(object);
                            setCurrentlyEditedRoleIndex(index);
                            openRoleRuleEditor();
                        }}
                    >
                        {t('SMP.ROLES.ROLE_RULE_WIZ.PERMISSION.ALLOW')}
                    </span>
                );
            case 'BLOCK':
                return (
                    <span
                        style={{ color: 'red', cursor: 'pointer' }}
                        onClick={() => {
                            setCurrentlyEditedRole(object);
                            setCurrentlyEditedRoleIndex(index);
                            openRoleRuleEditor();
                        }}
                    >
                        {t('SMP.ROLES.ROLE_RULE_WIZ.PERMISSION.BLOCK')}
                    </span>
                )
            default:
                return value;
        }
    });    

    return (
        <EditorLayout>
            <SMCTextInput
                width={600}
                value={description}
                onChange={e => setPageValue(pageSchema.description, e.target.value)}
                label={t('SMP.DESCRIPTION')}
            />
            <Sepperator />
            <EmptyRow />
            <SMCLabel bold fontSize="15px">{t('SMP.ROLES.GENERAL.ACCESS_CONTROL_POLICY')}</SMCLabel>
            <ButtonContainer>
                <SMCButton variant="ghost" icon="new" onClick={openRoleRuleEditor}>{t('SMC_GEN_NEW')}</SMCButton>
                <SMCButton variant="ghost" icon="delete" onClick={removeSelectedObjects}>{t('SMC_GEN_DELETE')}</SMCButton>
            </ButtonContainer>
            <SMCTable
                columns={[
                    { id: 'count',      header: '#',                                                                                                            width: 32,  align: "center", Cell: CountCell      },
                    { id: 'permission', header: t('SMP.ROLES.ROLE_RULE_WIZ.PERMISSION'),  accessor: rulesSchema.type,                                           width: 160,                  Cell: PermissionCell },
                    { id: 'operations', header: t('SMP.ROLES.ROLE_RULE_WIZ.OPERATIONS'),  accessor: rulesSchema.actions,                                        width: 180,                  Cell: OperationsCell },
                    { id: 'objectType', header: t('SMP.ROLES.ROLE_RULE_WIZ.OBJECT_TYPE'), accessor: [rulesSchema.subject, rulesSchema.subjectSchema.target],    width: 170,                  Cell: ObjectTypeCell },
                    { id: 'condition',  header: t('SMP.ROLES.ROLE_RULE_WIZ.CONDITION'),   accessor: [rulesSchema.subject, rulesSchema.subjectSchema.condition], width: 600                                        }
                ]}
                content={rules}
                selectedObjects={selectedObjects}
                setSelectedObjects={setSelectedObjects}
                rowIdSelector={(obj, index) => index + 1}
                hasCheckAll={true}
            />
            {/* Popup windows */}
            <RoleRuleEditor
                isOpen={isRoleRuleEditorOpen}
                onRequestClose={roleEditorOnCancel}
                defaultObject={currentlyEditedRole}
                onSuccess={roleEditorOnSuccess}
            />
        </EditorLayout>
    )
}

export default RoleGeneral;